/**
 * Проверка материала.
 *
 * Кнопки решения появляются только когда материал вынесен на проверку:
 * принимать черновик или повторно принимать принятое незачем. Журнал
 * проверок остаётся виден всегда — он объясняет, почему материал в текущем
 * состоянии.
 */

import { CheckCircle2Icon, Loader2Icon, RotateCcwIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { ItemReviewDto, ReviewDecision } from '@project-os/contracts';
import { getItemTypeDefinition, type ItemType } from '@project-os/contracts';
import { useReviewItem } from '@/api/assets';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Статус, из которого материал можно проверять. */
const READY_FOR_REVIEW = 'READY_FOR_REVIEW';

export function ReviewSection({
  projectId,
  itemId,
  itemType,
  status,
  reviews,
  canReview,
}: {
  projectId: string;
  itemId: string;
  itemType: ItemType;
  status: string;
  reviews: ItemReviewDto[];
  canReview: boolean;
}) {
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState<ReviewDecision | null>(null);
  const review = useReviewItem(projectId, itemId);

  const definition = getItemTypeDefinition(itemType);
  const readyLabel =
    definition.statuses.find((entry) => entry.value === READY_FOR_REVIEW)?.labelRu ?? 'На проверке';
  const isReady = status === READY_FOR_REVIEW;

  const decide = async (decision: ReviewDecision) => {
    setPending(decision);
    try {
      await review.mutateAsync({ decision, commentMd: comment.trim() || null });
      setComment('');
      toast.success(decision === 'APPROVED' ? 'Материал принят' : 'Отправлено на доработку');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось записать решение');
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="space-y-3">
      {canReview && isReady ? (
        <div className="space-y-2">
          <Textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={3}
            placeholder="Что именно нужно поправить — или почему материал принят. Необязательно."
            aria-label="Комментарий к проверке"
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={review.isPending} onClick={() => void decide('APPROVED')}>
              {pending === 'APPROVED' ? (
                <Loader2Icon className="animate-spin" />
              ) : (
                <CheckCircle2Icon />
              )}
              Принять
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={review.isPending}
              onClick={() => void decide('NEEDS_CHANGES')}
            >
              {pending === 'NEEDS_CHANGES' ? (
                <Loader2Icon className="animate-spin" />
              ) : (
                <RotateCcwIcon />
              )}
              Нужны правки
            </Button>
          </div>
        </div>
      ) : canReview ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          Решение принимается по материалу в статусе «{readyLabel}». Переведите его туда, когда он
          будет готов к просмотру.
        </p>
      ) : (
        <p className="text-xs leading-relaxed text-muted-foreground">
          Выносить решение по материалу может участник с правом проверки.
        </p>
      )}

      {reviews.length === 0 ? (
        <p className="text-xs text-muted-foreground">Материал ещё не проверяли.</p>
      ) : (
        <ul className="space-y-1.5">
          {reviews.map((entry) => (
            <li
              key={entry.id}
              className={cn(
                'surface-card border p-2.5',
                entry.decision === 'APPROVED'
                  ? 'border-tone-success-border'
                  : 'border-tone-warning-border',
              )}
            >
              <div className="flex items-center gap-2">
                <UserAvatar user={entry.reviewer} size="sm" />
                <span className="text-sm">{entry.reviewer.name}</span>
                <span
                  className={cn(
                    'text-xs font-medium',
                    entry.decision === 'APPROVED' ? 'text-tone-success' : 'text-tone-warning',
                  )}
                >
                  {entry.labelRu}
                </span>
                <div className="flex-1" />
                <span className="text-2xs text-muted-foreground">
                  {formatDateTime(entry.createdAt)}
                </span>
              </div>
              {entry.commentMd ? (
                <p className="mt-1.5 text-xs leading-relaxed whitespace-pre-wrap">
                  {entry.commentMd}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
